import { useMutation, useQueryClient } from "react-query";
import {
  bulkUpdateGrantIds,
  downloadGrantIdTemplate,
  getTransactionTemplate,
  parseGrantIdUpdateUpload,
  parseTransactionUpload,
  updateGrantTransactions,
  validateTransactions,
} from "../api/bulkGrantUpdate";
import {
  GrantIdUpdateRow,
  ParsedTransactionRow,
} from "../types/BulkGrantUpdate";

/**
 * Hook to download the grant transaction template
 */
export function useGetTransactionTemplate() {
  return useMutation({
    mutationKey: "getTransactionTemplate",
    mutationFn: (grantIds?: string[]) => getTransactionTemplate(grantIds),
  });
}

/**
 * Hook to parse and validate uploaded transaction file
 */
export function useParseTransactionUpload() {
  return useMutation({
    mutationKey: "parseTransactionUpload",
    mutationFn: (file: string) => parseTransactionUpload(file),
  });
}

/**
 * Hook to update grant transactions in bulk
 */
export function useUpdateGrantTransactions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: "updateGrantTransactions",
    mutationFn: (transactions: ParsedTransactionRow[]) =>
      updateGrantTransactions(transactions),
    onSuccess: () => {},
  });
}

/**
 * Hook to validate grant transactions in bulk
 */
export function useValidateTransactions() {
  return useMutation({
    mutationKey: "validateTransactions",
    mutationFn: (transactions: ParsedTransactionRow[]) =>
      validateTransactions(transactions),
  });
}

export function useBulkUpdateGrantIds() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: "bulkUpdateGrantIds",
    mutationFn: (updates: GrantIdUpdateRow[]) => bulkUpdateGrantIds(updates),
    onSuccess: () => {
      queryClient.invalidateQueries("allGrants");
    },
  });
}

export function useDownloadGrantIdTemplate() {
  return useMutation({
    mutationKey: "downloadGrantIdTemplate",
    mutationFn: (selectedGrantIds: string[]) =>
      downloadGrantIdTemplate(selectedGrantIds),
  });
}

export function useParseGrantIdUpdateUpload() {
  return useMutation({
    mutationKey: "parseGrantIdUpdateUpload",
    mutationFn: (file: string) => parseGrantIdUpdateUpload(file),
  });
}
